<template>
  <div class="definition">
    <div class="definition__title">
      <BaseSubHeading
        v-if="!change"
        className="definition__title_item"
        size="medium"
        weight="400"
      >
        Identify the steps that need to be completed for this effort to be marked as done.
      </BaseSubHeading>
      <BaseSubHeading
        v-if="change"
        className="definition__title_item"
        size="medium"
        weight="400"
      >
        Select "Definition of Done" criteria(s) that are appropriate to confirm the doneness of the
        effort.
      </BaseSubHeading>
      <button
        v-if="!change"
        class="definition__title_add"
        :class="{ 'is-disabled': isReadonly }"
        @click="onEdit"
      >
        <icon
          data="@icon/pen.svg"
          color="#336DFF"
          width="1.5em"
          height="1.5em"
        />
        Edit
      </button>
    </div>

    <transition name="transitionCheck">
      <ul
        v-if="!change"
        class="definition__content custom-scroll"
      >
        <li
          v-for="(item, index) in enabledDefinitions"
          :key="index"
          :style="getDefinitionStyle(index)"
        >
          <BasicCheckbox
            :key="`${index}-${definitionCheckboxVersion}`"
            :label="item.definition"
            :value="item.definition"
            :modelValue="item.done"
            class="definition__checkbox"
            :class="{ definition__checkbox_check: item.done }"
            :isDisabled="!itemId || isDefinitionReadOnly(item)"
            checkType="check"
            @update:modelValue="onCheckboxUpdate"
          />
        </li>
      </ul>
      <ul
        v-else
        class="definition__content custom-scroll"
      >
        <li
          v-for="(item, index) in allDefinitionsList"
          :key="index"
        >
          <BasicCheckbox
            :label="item.definition"
            :value="item.definition"
            :modelValue="item.enabled"
            :isDisabled="isMandatory(item)"
            class="definition__item_switch"
            checkType="switch"
            @update:modelValue="onSwitchUpdate"
          />
        </li>
      </ul>
    </transition>
    <AddBasicInput
      v-if="change"
      :rules="validators.validateDefinition(importDefinitionsList)"
      @enter="onAddCustomCheckbox"
    />
  </div>

  <template v-if="showCompleteButtons">
    <BaseSubHeading
      size="medium"
      weight="400"
      className="item-completed"
    >
      <BaseSubHeading
        v-if="!inActiveSprint && enableMarkAsCompleted"
        size="medium"
        weight="400"
        className="item-completed__description"
        color="monochrome-05"
      >
        Item must be in an Active Sprint to be marked as "Completed"
      </BaseSubHeading>
      <div v-else>&nbsp;</div>
      <button
        class="item-completed__button secondary-button definition__gradient-button"
        :disabled="
          !enableMarkAsCompleted || !inActiveSprint || item.status === 'OBSOLETE' || isReadonly
        "
        @click="$emit('itemCompleted')"
      >
        Mark as Completed
        <icon
          data="@icon/completed.svg"
          :fill="true"
          color="#4CAB26"
          width="1.25em"
          height="1.25em"
        />
      </button>
    </BaseSubHeading>
  </template>
  <div
    v-if="change"
    class="definition__buttons-wrapper"
  >
    <ControlButtons
      :small="true"
      @save="save"
      @cancel="cancel"
    />
  </div>
  <div
    v-else-if="!change && !itemId"
    class="definition__draft-clue"
  >
    <div class="definition__draft-clue__icon">
      <icon
        data="@icon/alert.svg"
        :fill="true"
        width="var(--icon-24)"
        height="var(--icon-24)"
        color="var(--serviceInfo-02)"
      />
    </div>
    <div class="definition__draft-clue__text">
      <BaseSubHeading
        size="medium"
        weight="400"
        color="monochrome-06"
      >
        "Definition of Done" checkboxes are currently disabled because this Backlog Item is in the
        unsaved Draft state.
      </BaseSubHeading>
      <BaseSubHeading
        size="medium"
        weight="400"
        color="monochrome-06"
      >
        Please save the Item to enable the checkboxes.
      </BaseSubHeading>
    </div>
  </div>
  <Teleport to="body">
    <ModalDefinitionDone
      :config="taskModalData"
      @confirm="onDefinitionModalConfirm"
    />
    <ModalDemoVideoRequired @confirm="onDemoVideoRequiredConfirm" />
    <ModalIncompleteAcceptanceCriteria
      :acceptanceCriteria="item.acceptanceCriteria"
      @confirm="onAcceptanceCriteriaModalConfirm"
      @cancel="onAcceptanceCriteriaModalCancel"
    />
  </Teleport>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop } from "vue-property-decorator"
import cloneDeep from "lodash/cloneDeep"
import { Action, Getter } from "s-vuex-class"

import ControlButtons from "@/components/ControlButtons/index.vue"
import AddBasicInput from "@/components/Form/AddBasicInput.vue"
import BasicCheckbox from "@/components/Form/BasicCheckbox.vue"
import ModalDefinitionDone from "@/components/Modal/ModalDefinitionDone.vue"
import ModalDemoVideoRequired from "@/components/Modal/ModalDemoVideoRequired.vue"
import ModalIncompleteAcceptanceCriteria from "@/components/Modal/ModalIncompleteAcceptanceCriteria.vue"

import {
  completeAcceptanceCriteriaChecklist,
  getAcceptanceCriteriaChecklistState
} from "@/helpers/acceptanceCriteriaChecklist"
import {
  DefinitionOfDone,
  DefinitionOfDoneProps,
  Item,
  ModalTypesForCratingTask,
  PostMortem,
  Task,
  TaskTypeOption
} from "@/models"
import { Actions } from "@/store/actions"
import { Getters } from "@/store/getters"
import validators, { validateDefinition } from "@/utils/validators"

import type { TaskType } from "@/models"

const editableDefinitionTaskTypes: TaskType[] = ["DISCOVERY"]

@Options({
  name: "DefinitionOfDoneComponent",
  methods: { validateDefinition },
  computed: {
    validators() {
      return validators
    }
  },
  components: {
    BasicCheckbox,
    AddBasicInput,
    ControlButtons,
    ModalDefinitionDone,
    ModalDemoVideoRequired,
    ModalIncompleteAcceptanceCriteria
  },
  emits: [
    "cancel",
    "fillPostMortem",
    "itemCompleted",
    "completeAcceptanceCriteria",
    "openDemoVideoModal",
    "save",
    "editTask",
    "updateAllList",
    "updateDefinitionOfDone",
    "confirmDefinitionOfDone"
  ]
})
export default class DefinitionOfDoneComponent extends Vue {
  @Prop({ required: true }) readonly item: Item
  @Prop({ default: "" }) readonly itemType: string
  @Prop({ default: false }) readonly isDone: boolean
  @Prop({ default: false }) readonly isReadonly: boolean
  @Prop({ default: false }) readonly inActiveSprint: boolean
  @Prop({ default: false }) readonly canAddFiveWhysByDefault: boolean
  @Prop({ default: [] }) readonly allDefinitionsList: DefinitionOfDone[]

  @Getter(Getters.GET_TASKS) readonly tasks: Task[]
  @Getter(Getters.GET_DEFINITIONS_LIST) readonly enabledDefinitions: DefinitionOfDone[]
  @Getter(Getters.GET_FIVE_WHYS) readonly postMortem: PostMortem

  @Action(Actions.GET_TASKS) getTasks: ({ itemId }: { itemId: string }) => void
  @Action(Actions.SET_MODAL_NEW_TASK_TYPE_OPTION) setNewTaskTypeOption: (
    taskTypeOption: TaskTypeOption | null
  ) => void
  @Action(Actions.SET_MODAL_TYPE_FOR_CREATING_NEW_TASK) onShowModalCreateTask: (
    payload: ModalTypesForCratingTask
  ) => void

  numberOfColumns = 2

  minNumberOfLines = 2

  change = false
  definitionCheckboxVersion = 0
  taskModalData = {
    definitionLabel: "",
    taskTypeLabel: "",
    tasks: [] as Task[],
    relatedTaskIds: [] as string[],
    relatedTaskCount: 0
  }

  importDefinitionsList: DefinitionOfDone[] = []

  get taskTypesList() {
    return Task.getTaskTypes(this.tasks)
  }

  get itemId() {
    return this.item?.id ?? ""
  }

  get enableMarkAsCompleted() {
    return !this.change && this.definitionsReadyToComplete && !this.isDone
  }

  get definitionsReadyToComplete() {
    return (
      !!this.enabledDefinitions.length &&
      this.enabledDefinitions.every(definition => definition.done)
    )
  }

  get showCompleteButtons() {
    return !this.change && this.itemId && !this.isDone
  }

  get hasDemoVideo() {
    return this.item.hasDemoVideo
  }

  async created() {
    const definitions = this.allDefinitionsList.map(def => {
      return DefinitionOfDone.create({
        ...def,
        enabled: this.isMandatory(def) ? true : def.enabled
      })
    })
    this.updateAllList(definitions)
    if (!this.itemId) {
      this.updateDefinitionOfDone(definitions.filter(def => def.enabled))
    }
  }

  isMandatory(def: DefinitionOfDoneProps): boolean {
    if (def.acceptanceCriteria) {
      return getAcceptanceCriteriaChecklistState(this.item.acceptanceCriteria).present
    }
    if (def.fiveWhys) {
      return this.canAddFiveWhysByDefault && (this.item.isSupportCase || this.item.isIncident)
    }

    let mandatory = false
    this.getDefinitionTypes(def).forEach(type => {
      if (this.taskTypesList.has(type)) {
        mandatory = true
      }
    })
    return mandatory
  }

  getDefinitionTypes(definition: DefinitionOfDoneProps): TaskType[] {
    const types = definition.types?.length
      ? definition.types
      : definition.type
        ? [definition.type]
        : []

    return Array.from(new Set(types))
  }

  /** A Dev definition covers Bug work; PR Review also belongs to it for Development teams. */
  getDefinitionBlockingTypes(definition: DefinitionOfDoneProps): TaskType[] {
    const types = this.getDefinitionTypes(definition)

    if (types.includes("DEV")) {
      const relatedTypes: TaskType[] =
        this.item.teamType === "DEVELOPMENT" ? ["BUG", "PR_REVIEW"] : ["BUG"]
      return Array.from(new Set([...types, ...relatedTypes]))
    }

    return types
  }

  isDefinitionReadOnly(definition: DefinitionOfDoneProps): boolean {
    const definitionTypes = this.getDefinitionTypes(definition)
    const definitionTasks = this.tasks.filter(
      task => !!task.id && definitionTypes.includes(task.typeParsed.id)
    )

    return !!definitionTasks.length && definitionTasks.every(task => task.readOnly)
  }

  formatTaskTypes(types: TaskType[]) {
    const labels = types.map(type => Task.getParsedType(type)?.label ?? type)

    if (labels.length <= 2) {
      return labels.join(" and ")
    }

    return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`
  }

  onCheckboxUpdate(value: boolean, label: string) {
    if (this.isReadonly) return
    const definition = this.enabledDefinitions.find(definition => definition.definition === label)

    if (!definition) return false

    if (definition.acceptanceCriteria && value) {
      const state = getAcceptanceCriteriaChecklistState(this.item.acceptanceCriteria)
      if (!state.complete) {
        this.$nextTick(() => {
          this.$modal.show("incomplete-acceptance-criteria-modal")
        })
        return false
      }
    }

    // If 5 Why's is checked, fill out the post mortem
    if (label === "5 Why's" && value) {
      this.$emit("fillPostMortem")
      return
    }

    if (label === "Demo Recorded" && !definition.done && !this.hasDemoVideo) {
      this.$modal.show("demo-video-required-modal")
      return false
    }

    const definitionTypes = this.getDefinitionTypes(definition)
    const taskToReopen = this.tasks.find(
      task =>
        definitionTypes.includes(task.typeParsed.id) &&
        editableDefinitionTaskTypes.includes(task.typeParsed.id)
    )
    if (!value && definition.done && taskToReopen?.id) {
      // Recreate the controlled checkbox because this click opens a modal instead of updating its model.
      this.definitionCheckboxVersion += 1
      this.$emit("editTask", {
        task: taskToReopen,
        definitionLabel: definition.definition
      })
      return false
    }
    const definitionBlockingTypes = this.getDefinitionBlockingTypes(definition)
    const relatedTasks = this.tasks.filter(
      task => !!task.id && definitionBlockingTypes.includes(task.typeParsed.id)
    )

    /** The confirmation is the only point where completing a DoD may change its related tasks. */
    if (value && !definition.done && relatedTasks.length > 0) {
      // BasicCheckbox keeps its own visual state until it is recreated.
      this.definitionCheckboxVersion += 1
      this.taskModalData = {
        definitionLabel: definition.definition,
        taskTypeLabel:
          definition.definition.replace(/(?:\s+(?:Done|Created|Tasks))+$/i, "") ||
          this.formatTaskTypes(definitionBlockingTypes),
        tasks: relatedTasks
          .filter(task => task.estimatedHours == null || Number(task.estimatedHours) > 0)
          .map(task => Task.create(task)),
        relatedTaskIds: relatedTasks.map(task => task.id).filter((id): id is string => !!id),
        relatedTaskCount: relatedTasks.length
      }
      this.$modal.show("definition-of-done-modal")
      return false
    }

    const relatedTaskToReopen = relatedTasks.find(
      task => definitionTypes.includes(task.typeParsed.id) && !task.readOnly
    )
    if (relatedTaskToReopen && definition.done) {
      if (relatedTaskToReopen.type === "BUG") {
        this.setNewTaskTypeOption({
          id: "DEV",
          label: "Dev"
        })
      } else {
        this.setNewTaskTypeOption(relatedTaskToReopen.typeParsed)
      }

      this.onShowModalCreateTask("DEFINITION")
      return false
    } else {
      const updateDefinition = this.enabledDefinitions.map(def =>
        DefinitionOfDone.create({ ...def, done: def.definition === label ? value : def.done })
      )
      this.updateDefinitionOfDone(updateDefinition)
    }
  }

  onDefinitionModalConfirm(config: { definitionLabel: string; relatedTaskIds: string[] }) {
    const id = config.definitionLabel
    const updateDefinition = this.enabledDefinitions.map(def =>
      DefinitionOfDone.create({ ...def, done: def.definition === id ? true : def.done })
    )
    this.$emit("confirmDefinitionOfDone", {
      definitions: updateDefinition,
      taskIds: config.relatedTaskIds
    })
  }

  onDemoVideoRequiredConfirm() {
    this.$emit("openDemoVideoModal")
  }

  onAcceptanceCriteriaModalConfirm() {
    const acceptanceCriteria = completeAcceptanceCriteriaChecklist(this.item.acceptanceCriteria)
    if (acceptanceCriteria) {
      this.$emit("completeAcceptanceCriteria", acceptanceCriteria)
    }
    const definitions = this.enabledDefinitions.map(definition =>
      DefinitionOfDone.create({
        ...definition,
        done: definition.acceptanceCriteria ? true : definition.done
      })
    )
    this.$modal.hide("incomplete-acceptance-criteria-modal")
    this.updateDefinitionOfDone(definitions)
  }

  onAcceptanceCriteriaModalCancel() {
    this.$modal.hide("incomplete-acceptance-criteria-modal")
  }

  onSwitchUpdate(value: boolean, id: string) {
    const definition = [...this.allDefinitionsList].find(definition => definition.definition === id)
    if (definition) {
      definition.enabled = value
      definition.manual = true
      this.importDefinitionsList.find(def => def.definition === definition.definition)
        ? (this.importDefinitionsList = this.importDefinitionsList.filter(
            def => def.definition !== definition.definition
          ))
        : this.importDefinitionsList.push(definition)
    }
  }

  async onAddCustomCheckbox(definition: string) {
    const newDefinition = DefinitionOfDone.create({
      definition: definition.trim(),
      done: false,
      enabled: true,
      manual: true
    })
    this.updateAllList([...this.allDefinitionsList, newDefinition])
    this.importDefinitionsList.push(newDefinition)
  }

  @Emit()
  save() {
    this.change = false
    this.updateDefinitionOfDone(this.importDefinitionsList)
    this.importDefinitionsList = []
  }

  @Emit()
  cancel() {
    this.change = false
    this.importDefinitionsList = []
  }

  onEdit() {
    if (this.isReadonly) return
    this.importDefinitionsList = cloneDeep(this.enabledDefinitions)
    this.change = true
  }

  getDefinitionStyle(index: number) {
    let maxLinesForColumn =
      Math.ceil(this.enabledDefinitions.length / (this.minNumberOfLines * this.numberOfColumns)) *
      this.minNumberOfLines
    return `grid-column-start: ${index < maxLinesForColumn ? 1 : 2}; grid-row-start: ${index < maxLinesForColumn ? 0 : index - (maxLinesForColumn - 1)}`
  }

  @Emit()
  updateDefinitionOfDone(definitions: DefinitionOfDone[]) {
    return definitions
  }

  @Emit()
  updateAllList(definitions: DefinitionOfDone[]) {
    return definitions
  }
}
</script>
